import {FC, useMemo} from 'react'

import {JurisdictionTooltipRow} from '@/components/common/jurisdiction/JurisdictionTooltipRow'
import {LocalizedNumberFormat} from '@/components/common/LocalizedNumberFormat'
import {usePreferences} from '@/components/context/Preferences'
import {useFormSummaryContent} from '@/content/formSummary'
import {getPricingContent} from '@/content/pricing'
import {useTranslation} from '@/i18n'
import {Jurisdiction} from '@/utils/countries/constants'
import {
  calculateTmOwnershipTransferAdditionalTrademarkFee,
  calculateTmOwnershipTransferCountryPrice,
} from '@/utils/pricing/tm-ownership-transfer/calculations'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type JurisdictionTooltipProps = {
  jurisdiction: Jurisdiction
}

export const JurisdictionTooltip: FC<JurisdictionTooltipProps> = ({jurisdiction}) => {
  const {t} = useTranslation()
  const {serviceFeeTitle, administrativeFeeTitle, freeTitle} = useFormSummaryContent()
  const {trademarksIncludedTitle, additionalTrademarkFeeTitle} =
    getPricingContent().trademarkOwnershipTransfer
  const {currency} = usePreferences()
  const price = useMemo(
    () => calculateTmOwnershipTransferCountryPrice(jurisdiction, currency),
    [jurisdiction, currency],
  )
  const additionalTrademarkFee = useMemo(
    () => calculateTmOwnershipTransferAdditionalTrademarkFee(jurisdiction, currency),
    [jurisdiction, currency],
  )

  return (
    <>
      <div className="breakdown">
        <JurisdictionTooltipRow
          title={t(serviceFeeTitle)}
          number={price.serviceFee.total.vatBase}
        />
        <JurisdictionTooltipRow
          title={t(administrativeFeeTitle)}
          number={price.legalFee?.total.vatBase ?? 0}
          note={<LocalizedNumberFormat value={0} type="currency" currency={currency} />}
        />
        <JurisdictionTooltipRow title={t(trademarksIncludedTitle)} number={1} numberType="number" />
        {additionalTrademarkFee !== null && (
          <JurisdictionTooltipRow
            title={t(additionalTrademarkFeeTitle)}
            number={additionalTrademarkFee}
            note={t(freeTitle)}
          />
        )}
      </div>

      <style jsx>{`
        @media ${TABLET_SCREENSIZE} {
          .breakdown {
            min-width: 350px;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .breakdown {
            min-width: inherit;
          }
        }
      `}</style>
    </>
  )
}
